import { useState, useRef } from 'react'
import { Upload, Download, X, Crop } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ImageCropper() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [imageSrc, setImageSrc] = useState(null)
  const [croppedImage, setCroppedImage] = useState(null)
  const [isCropping, setIsCropping] = useState(false)
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [startPos, setStartPos] = useState(null)
  const canvasRef = useRef(null)
  const imageRef = useRef(null)
  const containerRef = useRef(null)
  const fileInputRef = useRef(null)

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      setImageSrc(e.target.result)
      setSelectedFile(file)
      setCroppedImage(null)
      setCropArea({ x: 0, y: 0, width: 0, height: 0 })
    }
    reader.readAsDataURL(file)
  }

  const handleMouseDown = (e) => {
    if (!imageSrc) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setStartPos({ x, y })
    setIsCropping(true)
  }

  const handleMouseMove = (e) => {
    if (!isCropping || !startPos || !imageSrc) return
    const rect = containerRef.current.getBoundingClientRect()
    const currentX = e.clientX - rect.left
    const currentY = e.clientY - rect.top

    const x = Math.min(startPos.x, currentX)
    const y = Math.min(startPos.y, currentY)
    const width = Math.abs(currentX - startPos.x)
    const height = Math.abs(currentY - startPos.y)

    setCropArea({ x, y, width, height })
  }

  const handleMouseUp = () => {
    setIsCropping(false)
    setStartPos(null)
  }

  const performCrop = () => {
    if (!imageSrc || cropArea.width === 0 || cropArea.height === 0) {
      alert('Please select an area to crop')
      return
    }

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      // Calculate scale factor
      const scaleX = img.width / containerRef.current.offsetWidth
      const scaleY = img.height / containerRef.current.offsetHeight

      // Calculate actual crop coordinates
      const sx = cropArea.x * scaleX
      const sy = cropArea.y * scaleY
      const sw = cropArea.width * scaleX
      const sh = cropArea.height * scaleY

      canvas.width = sw
      canvas.height = sh

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh)

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob)
        setCroppedImage({ url, blob })
      }, selectedFile.type, 0.95)
    }
    img.src = imageSrc
  }

  const downloadImage = () => {
    if (!croppedImage) return

    const link = document.createElement('a')
    link.href = croppedImage.url
    link.download = `cropped-${selectedFile.name}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const clearFile = () => {
    setSelectedFile(null)
    setImageSrc(null)
    setCroppedImage(null)
    setCropArea({ x: 0, y: 0, width: 0, height: 0 })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      <Card
        className="cursor-pointer border-gray-300 hover:border-primary-400"
        onClick={() => fileInputRef.current?.click()}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <Upload className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Select an image to crop</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {imageSrc && (
        <>
          <Card>
            <Card.Body>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Click and drag on the image to select the area you want to crop
                </p>
              </div>

              <div
                ref={containerRef}
                className="relative border-2 border-gray-300 rounded-lg overflow-hidden cursor-crosshair"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ maxHeight: '500px', overflow: 'auto' }}
              >
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt="To crop"
                  className="max-w-full h-auto"
                  draggable={false}
                />
                {cropArea.width > 0 && cropArea.height > 0 && (
                  <div
                    className="absolute border-2 border-primary-600 bg-primary-200 bg-opacity-20"
                    style={{
                      left: `${cropArea.x}px`,
                      top: `${cropArea.y}px`,
                      width: `${cropArea.width}px`,
                      height: `${cropArea.height}px`,
                      pointerEvents: 'none',
                    }}
                  >
                    <div className="absolute top-0 left-0 w-3 h-3 border-2 border-primary-600 bg-white"></div>
                    <div className="absolute top-0 right-0 w-3 h-3 border-2 border-primary-600 bg-white"></div>
                    <div className="absolute bottom-0 left-0 w-3 h-3 border-2 border-primary-600 bg-white"></div>
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-2 border-primary-600 bg-white"></div>
                  </div>
                )}
              </div>

              <Button onClick={performCrop} variant="primary" className="w-full mt-4">
                <Crop className="w-4 h-4 mr-2" />
                Crop Image
              </Button>
            </Card.Body>
          </Card>

          {croppedImage && (
            <Card>
              <Card.Body>
                <div className="text-center space-y-4">
                  <div className="inline-block p-4 bg-gray-100 rounded-lg">
                    <img src={croppedImage.url} alt="Cropped" className="max-w-full h-auto max-h-96" />
                  </div>
                  <Button onClick={downloadImage} variant="primary" size="lg" className="w-full">
                    <Download className="w-5 h-5 mr-2" />
                    Download Cropped Image
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

